import React, { createContext, useCallback, useMemo, useState } from 'react';

import { parameters, setDomain, setLocale } from '../localization/strings';
import { getLocaleConfig, getRegionFromUrl } from '../helpers/locale-helper';

interface ComponentProps {
  children: React.ReactNode;
}

interface ContextValue {
  theme: string;
  isAllergomedica: boolean;
  isfoodId: boolean;
  locale: string;
  hasConsultation: boolean;
  hasPersonCode: boolean;
  hasPresale: boolean;
  hasBirthDate: boolean;
  hasDoctor: boolean;
  productType: string;
  setProductType: (type: string) => void;
  isSelfCo: boolean;
  region: 'eu' | 'uk' | 'us' | null;
  currency: 'EUR' | 'GBP' | 'PLN' | 'USD';
}

const SiteContext = createContext<ContextValue>({
  theme: '',
  isAllergomedica: false,
  isfoodId: false,
  locale: '',
  hasConsultation: false,
  hasPersonCode: false,
  hasPresale: false,
  hasBirthDate: false,
  hasDoctor: false,
  productType: '',
  setProductType: () => null,
  isSelfCo: false,
  region: null,
  currency: 'EUR',
});

function SiteContextProvider({ children }: ComponentProps) {
  const host = window.location.hostname;

  // Universal theme detection
  const getThemeFromHost = (hostname: string): string => {
    // For local development, use REACT_APP_THEME from .env file
    if (hostname === 'localhost' && process.env.REACT_APP_THEME) {
      return process.env.REACT_APP_THEME;
    }

    switch (true) {
      case hostname.includes('allergomedica'):
        return 'allergomedica';
      case hostname.includes('self.co'):
        return 'selfCo';
      default:
        return 'foodId';
    }
  };

  const theme = getThemeFromHost(host);

  const [product, setProduct] = useState<string>('allergyTest');

  const setProductType = useCallback(
    (item) => {
      setProduct(item);
    },
    [setProduct],
  );

  // Get locale configuration for this domain
  const { validLocales, defaultLocale } = getLocaleConfig(host, theme);

  // Check if URL path contains a language
  const urlObj = new URL(window.location.href);
  const pathSegments = urlObj.pathname.split('/');

  // For Self-Co with region routing, locale is at position [1] after region
  const isSelfCo = theme === 'selfCo';
  const region = isSelfCo ? getRegionFromUrl() : null;
  const localePosition = isSelfCo && region ? 1 : 0;
  const langFromUrl = pathSegments[localePosition + 1];

  // Use URL language if valid, otherwise use default
  const locale = langFromUrl && validLocales.includes(langFromUrl) ? langFromUrl : defaultLocale;

  const isfoodId = theme === 'foodId';
  const isAllergomedica = theme === 'allergomedica';

  // Determine currency: Self-Co uses region, others use locale
  const getCurrency = (): 'EUR' | 'GBP' | 'PLN' | 'USD' => {
    if (isSelfCo && region === 'uk') return 'GBP';
    if (isSelfCo && region === 'us') return 'USD';
    if (locale === 'pl') return 'PLN';

    return 'EUR';
  };

  const currency = getCurrency();

  setLocale(locale);

  setDomain(theme);

  const contextValue = useMemo(
    () => ({
      theme,
      locale,
      isAllergomedica,
      isfoodId,
      hasConsultation: parameters().features.hasConsultation,
      hasPersonCode: parameters().features.hasPersonCode,
      hasPresale: parameters().features.hasPresale,
      hasBirthDate: parameters().features.hasBirthDate,
      hasDoctor: parameters().features.hasDoctor,
      productType: product,
      setProductType,
      isSelfCo,
      region,
      currency,
    }),
    [theme, isAllergomedica, isfoodId, locale, product, setProductType, isSelfCo, region, currency],
  );

  return <SiteContext.Provider value={contextValue}>{children}</SiteContext.Provider>;
}

export { SiteContext, SiteContextProvider };
